import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslatePipe, TranslationService } from '@nebula-shared/data-access';
import { ButtonComponent, IconComponent } from 'nebula-ui-kit';

export interface ExportFormatOption {
  value: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any;
}

export interface ExportRequestEvent {
  format: string;
  params?: Record<string, string | number | boolean | null | undefined>;
}

@Component({
  selector: 'app-export-format-selector',
  standalone: true,
  imports: [CommonModule, ButtonComponent, TranslatePipe, IconComponent],
  templateUrl: './export-format-selector.component.html',
  styleUrls: ['./export-format-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportFormatSelectorComponent {
  label = input('Exportar');
  options = input<ExportFormatOption[]>([]);
  optionsWithText = computed(() =>
    this.options().map((option) => ({
      ...option,
      text: option.label === 'csv' ? 'COMMON.EXPORT.MESSAGE.CSV' : 'COMMON.EXPORT.MESSAGE.EXCEL',
      icon: option.label === 'csv' ? ('FileSpreadsheet' as const) : ('FileX' as const),
    })),
  );
  disabled = input(false);
  loading = input(false);
  requestParams = input<Record<string, string | number | boolean | null | undefined> | undefined>(
    undefined,
  );
  private readonly translationService = inject(TranslationService);

  exportSelected = output<string>();
  exportRequested = output<ExportRequestEvent>();

  open = signal(false);
  selectedFormat = signal('');

  constructor() {
    effect(() => {
      const list = this.options();
      const current = this.selectedFormat();
      if (!list.length) {
        this.selectedFormat.set('');
        return;
      }
      const exists = list.some((x) => x.value === current);
      if (!exists) {
        this.selectedFormat.set(list[0].value);
      }
    });
  }

  toggleMenu(): void {
    if (this.disabled() || this.loading() || this.options().length === 0) return;
    this.open.update((v) => !v);
  }

  onFormatChange(value: string): void {
    this.selectedFormat.set(value);
  }

  cancel(): void {
    if (this.loading()) return;
    this.open.set(false);
  }

  confirmDownload(): void {
    const value = this.selectedFormat().trim();
    if (!value) return;
    this.open.set(false);
    this.exportSelected.emit(value);
    this.exportRequested.emit({ format: value, params: this.requestParams() });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.open() || this.loading()) return;
    this.open.set(false);
  }
}
